import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import adminService from '../services/adminService';
import { useAuth } from '../context/AuthContext';

const AdminUserSwitch = () => {
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [switchingUserId, setSwitchingUserId] = useState('');
  const [error, setError] = useState('');

  const fetchUsers = async (term = '') => {
    setLoading(true);
    setError('');
    try {
      const response = await adminService.getSwitchableUsers(term);
      setUsers(response.users || []);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearchSubmit = async (event) => {
    event.preventDefault();
    await fetchUsers(search);
  };

  const handleImpersonate = async (userId) => {
    setError('');
    setSwitchingUserId(userId);
    try {
      const data = await adminService.impersonateUser(userId);
      applySession({ token: data.token, user: data.user });
      navigate('/dashboard');
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to switch user');
    } finally {
      setSwitchingUserId('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">User Switch</h1>
          <p className="mt-1 text-sm text-gray-600">
            Switch into a non-admin user account to view the app through their eyes.
          </p>
        </div>

        <form onSubmit={handleSearchSubmit} className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Search
            </button>
          </div>
        </form>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500">Loading users...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500">No users found.</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id}>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.title || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleImpersonate(user._id)}
                        disabled={switchingUserId === user._id}
                        className="px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:bg-primary-400 disabled:cursor-not-allowed"
                      >
                        {switchingUserId === user._id ? 'Switching...' : 'Switch to user'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminUserSwitch;
